import { ColumnProps } from '@uma/react-commons/dist/components/Card/CardWrapper/CardColumns/index';
import { CardRowProps } from '@uma/react-commons/dist/components/Card/CardWrapper/CardRow/index';
import {
  Card,
  ROW_TYPE,
  useReadOnlyDataStreamer,
  ErrorBoundary
} from '@uma/react-commons/dist/index';
import React, { useCallback, useMemo, useState } from 'react';
import { FeatureFlagProps } from 'src/App';
import {
  ChangePasswordControlButtonClicked,
  ChangePasswordHyperLinkClicked,
  LoginDetailsError,
  ManageEmailControlButtonClicked,
  ManageEmailHyperLinkClicked,
  ManagePhoneControlButtonClicked,
  ManagePhoneHyperLinkClicked,
  ManageTwoFactorAuthHyperlinkClicked,
  ManageTwoFactorAuthControlButtonClicked,
  ManageLoginMethodsControlButtonClicked,
  ManageLoginMethodsHyperLinkClicked
} from './analytics';
import { FormTypesEnum } from 'src/utils/enums';
import { useStaticStrings } from 'src/utils/strings';
import { URLS } from 'src/utils/urls';
import Loader from '../Loader';
import Verified from '../Verified';
import { Value, ValueContent } from './styles';
import { useDependencyManagerContext } from 'src/contexts/dependencyManager';
import { getFieldsToFetchProfile } from 'src/utils/getFieldsToFetch';

interface ValueColumnProps {
  value: string;
  verified?: boolean | null;
  dataComponentName?: string;
}

const ValueColumn = ({
  value,
  verified,
  dataComponentName
}: ValueColumnProps) => {
  return (
    <Value>
      <ValueContent
        {...(dataComponentName ? { 'data-component': dataComponentName } : {})}
      >
        {value}
      </ValueContent>
      {verified != null && <Verified verified={verified} />}
    </Value>
  );
};

export interface LoginDetailsType {
  email: string;
  emailVerified: boolean | null;
  phone: string;
  phoneVerified: boolean | null;
  username: string;
  mfa: boolean | null;
}

function formatPhoneNumber(phone: string, emptyPhone: string): string {
  try {
    if (!phone) return emptyPhone;
    const phoneObj = JSON.parse(phone);
    if (phoneObj.country_code && phoneObj.national_number) {
      return `${phoneObj.country_code} ${phoneObj.national_number}`;
    }
    return emptyPhone;
  } catch {
    return emptyPhone;
  }
}

const LoginDetails = (
  featureFlagProps: FeatureFlagProps
): React.JSX.Element => {
  const { translate } = useDependencyManagerContext();

  const [hpIdUser, setHpIdUser] = useState<LoginDetailsType>({
    email: '',
    emailVerified: null,
    phone: '',
    phoneVerified: null,
    username: '',
    mfa: null
  });

  const { error, loading } = useReadOnlyDataStreamer(
    setHpIdUser,
    getFieldsToFetchProfile
  );

  const {
    fsDataComponentName,
    loginDetailsTitle,
    emailLabel,
    emptyEmail,
    phoneLabel,
    emptyPhone,
    loginMethodsLabel,
    emailAndPhoneLabel,
    emailAndUsernameLabel,
    emailAndPhoneAndUsernameLabel,
    passwordMask,
    passwordLabel,
    mfaLabel
  } = useStaticStrings();

  const mfaValue = useMemo(() => {
    if (hpIdUser.mfa === null) {
      return mfaLabel;
    }

    const key = hpIdUser.mfa
      ? 'security.twoFactorAuth.on'
      : 'security.twoFactorAuth.off';

    const defaultValue = hpIdUser.mfa
      ? '2-factor authentication is on'
      : '2-factor authentication is off';

    return translate(key, defaultValue);
  }, [mfaLabel, hpIdUser.mfa, translate]);

  const {
    securityHPIDEmail: isSecurityHpIdEmailEnabled,
    securityHPIDPassword: isSecurityHpIdPasswordEnabled,
    securityHPIDPhone: isSecurityHpIdPhoneEnabled,
    securityHPIDMfa: isSecurityHpIdMfaEnabled,
    securityHpIdMfaEnableRedirect: isSecurityHpIdMfaEnableRedirectEnabled,
    securityLoginMethodsFieldEnabled: isSecurityLoginMethodsFieldEnabled,
    securityLoginMethodsRedirectEnabled: isSecurityLoginMethodsRedirectEnabled,
    isLoading: loadingFeatureFlags
  } = featureFlagProps;

  const handleHPIDPhone = useCallback(
    (phone: string): string | null => {
      if (!phone) return null;
      const currentValue = formatPhoneNumber(phone, emptyPhone);
      return currentValue !== emptyPhone ? currentValue : null;
    },
    [emptyPhone]
  );

  const identifiersFromAliases = useMemo(() => {
    if (!hpIdUser.username) return '';

    try {
      const usernameObj = JSON.parse(hpIdUser.username);
      const aliases = usernameObj.aliases || [];

      const hasEmail = aliases.includes(hpIdUser.email);

      const formattedPhone = handleHPIDPhone(hpIdUser.phone);
      const cleanedPhone = formattedPhone
        ? formattedPhone.replace(/[\s-]/g, '')
        : '';

      const hasPhone = aliases.includes(cleanedPhone);

      const hasUsername = aliases.some(
        (alias: string) => alias !== hpIdUser.email && alias !== cleanedPhone
      );

      if (hasEmail && hasPhone && hasUsername)
        return emailAndPhoneAndUsernameLabel;
      if (hasEmail && hasPhone) return emailAndPhoneLabel;
      if (hasEmail && hasUsername) return emailAndUsernameLabel;
      if (hasEmail) return emailLabel;
      return '';
    } catch {
      return '';
    }
  }, [
    hpIdUser.username,
    hpIdUser.phone,
    hpIdUser.email,
    emailLabel,
    emailAndPhoneLabel,
    emailAndUsernameLabel,
    emailAndPhoneAndUsernameLabel,
    handleHPIDPhone
  ]);

  if (error) {
    return (
      <Card
        title={{
          content: loginDetailsTitle
        }}
        rowsColorChangeEnabled={false}
        childrenStyle={{ align: 'center', marginTop: '16px' }}
        data-testid="error-section"
      >
        <ErrorBoundary
          analyticsEvent={LoginDetailsError('HP ID Widgets error')}
          loading={false}
          onRetry={() => window.location.reload()}
        />
      </Card>
    );
  }

  if (loading || loadingFeatureFlags) {
    return <Loader />;
  }

  const emailColumns: ColumnProps[] = [];
  const phoneColumns: ColumnProps[] = [];
  const loginDetailsData = {
    email: hpIdUser.email,
    emailVerified: String(hpIdUser.emailVerified).toLowerCase() === 'true',
    phone: handleHPIDPhone(hpIdUser.phone),
    phoneVerified: String(hpIdUser.phoneVerified).toLowerCase() === 'true'
  };

  const getLinkType = (flag: boolean): ROW_TYPE => {
    return flag ? ROW_TYPE.INTERNAL : ROW_TYPE.EXTERNAL;
  };

  emailColumns.push({ value: emailLabel });
  emailColumns.push({
    value: loginDetailsData.email ? (
      <ValueColumn
        value={loginDetailsData.email}
        verified={loginDetailsData.emailVerified}
        dataComponentName={fsDataComponentName}
      />
    ) : (
      emptyEmail
    )
  });

  phoneColumns.push({ value: phoneLabel });
  phoneColumns.push({
    value: loginDetailsData.phone ? (
      <ValueColumn
        value={loginDetailsData.phone}
        verified={loginDetailsData.phoneVerified}
        dataComponentName={fsDataComponentName}
      />
    ) : (
      emptyPhone
    )
  });

  const rows = [
    {
      index: 0,
      content: emailColumns,
      type: getLinkType(isSecurityHpIdEmailEnabled),
      link: {
        url: URLS.HpIdSecurity,
        pathname: `/security/${FormTypesEnum.Email}`,
        dataTestId: 'ManageEmailLink'
      },
      analyticsEvent: isSecurityHpIdEmailEnabled
        ? ManageEmailControlButtonClicked
        : ManageEmailHyperLinkClicked
    },
    {
      index: 1,
      content: phoneColumns,
      type: getLinkType(isSecurityHpIdPhoneEnabled),
      link: {
        url: URLS.HpIdSecurity,
        pathname: `/security/${FormTypesEnum.Phone}`,
        dataTestId: 'ManagePhoneLink'
      },
      analyticsEvent: isSecurityHpIdPhoneEnabled
        ? ManagePhoneControlButtonClicked
        : ManagePhoneHyperLinkClicked
    },
    isSecurityLoginMethodsFieldEnabled && {
      index: 2,
      content: [
        { value: loginMethodsLabel },
        { value: identifiersFromAliases }
      ],
      type: getLinkType(isSecurityLoginMethodsRedirectEnabled),
      link: {
        url: URLS.HpIdSecurity,
        pathname: `/security/${FormTypesEnum.UserName}`,
        dataTestId: 'ManageLoginMethodsLink'
      },
      analyticsEvent: isSecurityLoginMethodsRedirectEnabled
        ? ManageLoginMethodsControlButtonClicked
        : ManageLoginMethodsHyperLinkClicked
    },
    {
      index: 3,
      content: [{ value: passwordLabel }, { value: passwordMask }],
      type: getLinkType(isSecurityHpIdPasswordEnabled),
      link: {
        url: URLS.HpIdSecurity,
        pathname: `/security/${FormTypesEnum.Password}`,
        dataTestId: 'ChangePasswordLink'
      },
      analyticsEvent: isSecurityHpIdPasswordEnabled
        ? ChangePasswordControlButtonClicked
        : ChangePasswordHyperLinkClicked
    },
    isSecurityHpIdMfaEnabled && {
      index: 4,
      content: [
        { value: mfaLabel },
        {
          value: hpIdUser.mfa !== null ? mfaValue : mfaLabel
        }
      ],
      type: getLinkType(isSecurityHpIdMfaEnableRedirectEnabled),
      link: {
        url: URLS.HpIdSecurity,
        pathname: `/security/${FormTypesEnum.Mfa}`,
        dataTestId: 'ManageMfaLink'
      },
      analyticsEvent: isSecurityHpIdMfaEnableRedirectEnabled
        ? ManageTwoFactorAuthControlButtonClicked
        : ManageTwoFactorAuthHyperlinkClicked
    }
  ] as CardRowProps[];

  const rowsCleaned = rows.filter((row) => row != null && row.content != null);
  return (
    <Card
      title={{ content: loginDetailsTitle }}
      rows={rowsCleaned}
    />
  );
};

export default LoginDetails;
